'use client';

import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { MemoSimulator, EditPlan } from '@/lib/memo/memo-simulator';

interface MemoPreviewPlayerProps {
  playbackId: string;
  startTime: number;  // seconds
  endTime: number;    // seconds
  editPlan: EditPlan;
  vttUrl?: string;    // Original VTT URL
  autoPlay?: boolean;
  onTimeUpdate?: (currentTime: number) => void;  // Callback for video time changes
  className?: string;
}

/**
 * MemoPreviewPlayer
 * Simulates video edits instantly using MUX player + client-side manipulation
 * - Skip-range playback (jumps over cuts)
 * - Modified caption injection
 * - Visual feedback for simulation
 */
export function MemoPreviewPlayer({
  playbackId,
  startTime,
  endTime,
  editPlan,
  vttUrl,
  autoPlay = true,
  onTimeUpdate,
  className = '',
}: MemoPreviewPlayerProps) {
  const playerRef = useRef<HTMLDivElement>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [simulator] = useState(() => new MemoSimulator(startTime, endTime, editPlan));
  const cleanupRef = useRef<(() => void) | null>(null);

  // Expose seekTo method via ref (for parent components)
  useEffect(() => {
    if (videoElRef.current && (playerRef.current as any)?.seekTo === undefined) {
      (playerRef.current as any).seekTo = (time: number) => {
        if (videoElRef.current) {
          console.log('[MemoPreviewPlayer] Seeking to:', time, 's');
          videoElRef.current.currentTime = time;
        }
      };
    }
  }, [videoElRef.current]);

  // Load MUX player script
  useEffect(() => {
    if (!document.querySelector('script[src*="mux-player"]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@mux/mux-player@2.4.1/dist/mux-player.js';
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
    }
  }, []);

  // Setup player with simulation
  useEffect(() => {
    if (!scriptLoaded || !playerRef.current) return;

    playerRef.current.innerHTML = '';

    const muxPlayerEl = document.createElement('mux-player') as any;
    muxPlayerEl.setAttribute('playback-id', playbackId);
    muxPlayerEl.setAttribute('start-time', startTime.toString());
    if (autoPlay) muxPlayerEl.setAttribute('autoplay', '');
    muxPlayerEl.setAttribute('controls', '');
    muxPlayerEl.setAttribute('stream-type', 'on-demand');
    muxPlayerEl.setAttribute('preload', 'metadata');
    muxPlayerEl.style.width = '100%';
    muxPlayerEl.style.height = '100%';

    playerRef.current.appendChild(muxPlayerEl);

    // Wait for player to be ready, then setup simulation
    const setupSimulation = () => {
      const videoEl = muxPlayerEl.querySelector('video') || muxPlayerEl.media;

      if (!videoEl) {
        console.warn('[MemoPreviewPlayer] Video element not found, retrying...');
        setTimeout(setupSimulation, 500);
        return;
      }

      console.log('[MemoPreviewPlayer] Installing skip-range listener');

      // Store video element reference for seeking
      videoElRef.current = videoEl;

      // Install skip listener
      const cleanup = simulator.installSkipListener(videoEl);
      cleanupRef.current = cleanup;

      // Add timeupdate listener for transcript sync AND boundary enforcement
      if (onTimeUpdate) {
        const handleTimeUpdate = () => {
          const currentTime = videoEl.currentTime;

          // Enforce memo boundaries - loop at end
          if (currentTime > endTime) {
            videoEl.currentTime = startTime;
            return; // Don't call onTimeUpdate for boundary jump
          }

          // Prevent seeking before start (edge case)
          if (currentTime < startTime - 0.5) {
            videoEl.currentTime = startTime;
            return;
          }

          onTimeUpdate(currentTime);
        };
        videoEl.addEventListener('timeupdate', handleTimeUpdate);

        // Add to cleanup
        const originalCleanup = cleanup;
        cleanupRef.current = () => {
          originalCleanup();
          videoEl.removeEventListener('timeupdate', handleTimeUpdate);
        };
      }

      // Inject modified captions if available
      if (vttUrl && editPlan.captions && editPlan.captions.length > 0) {
        fetch(vttUrl)
          .then(res => res.text())
          .then(vttContent => {
            const modifiedVTTUrl = simulator.createVTTBlobURL(vttContent);

            const track = document.createElement('track');
            track.kind = 'subtitles';
            track.label = 'Edited';
            track.srclang = 'en';
            track.src = modifiedVTTUrl;
            track.default = true;

            videoEl.appendChild(track);

            console.log('[MemoPreviewPlayer] Injected modified captions');
          })
          .catch(err => console.error('[MemoPreviewPlayer] Failed to load VTT:', err));
      }
    };

    // Wait for player to mount
    setTimeout(setupSimulation, 1000);

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
      if (playerRef.current) {
        playerRef.current.innerHTML = '';
      }
    };
  }, [scriptLoaded, playbackId, startTime, autoPlay, editPlan, vttUrl]);

  const effectiveDuration = simulator.getEffectiveDuration();
  const cutCount = editPlan.cuts?.length || 0;
  const captionEditCount = editPlan.captions?.length || 0;

  return (
    <div className={`relative ${className}`}>
      {/* Simulation Badge */}
      <Badge
        variant="secondary"
        className="absolute top-4 right-4 z-10 bg-accent-blue-100 text-accent-blue-700 dark:bg-accent-blue-900/50 dark:text-accent-blue-300 font-medium"
      >
        Simulated Preview
      </Badge>

      {/* Edit Info */}
      <div className="absolute top-4 left-4 z-10 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-white space-y-1">
        <div>Duration: {effectiveDuration.toFixed(1)}s (cut {(endTime - startTime - effectiveDuration).toFixed(1)}s)</div>
        {cutCount > 0 && <div>{cutCount} cut{cutCount > 1 ? 's' : ''}</div>}
        {captionEditCount > 0 && <div>{captionEditCount} caption edit{captionEditCount > 1 ? 's' : ''}</div>}
      </div>

      {/* MUX Player Container */}
      <div
        ref={playerRef}
        className="w-full h-full bg-black"
        data-testid="memo-preview-player"
      />
    </div>
  );
}
